import { useEffect, useMemo, useRef, useState } from "react";
import { ReplaySource, type Recording } from "@radar-sim/engine";
import type { WorldSnapshot } from "@radar-sim/schema";
import type { AreaData } from "@radar-sim/area-data";
import { PPI } from "../ppi/PPI.js";
import { ChartView } from "../chart/ChartView.js";
import {
  DEFAULT_DISPLAY_STATE,
  type DisplayState,
} from "../ppi/displayState.js";

/**
 * Solo-mode debrief surface. Takes a saved Recording, drives a
 * ReplaySource, and renders the same PPI + chart components the
 * live scenario uses — but without helm controls, MARPA acquire,
 * vessel-add, etc. (replay-source rejects mutating commands).
 *
 * Adds a timeline scrubber + play/pause/speed controls keyed to
 * the recording's snapshot stream. The student can scrub to any
 * point in the run and see exactly what their radar painted at
 * that moment.
 */
export function SoloDebrief(props: {
  recording: Recording;
  area: AreaData | undefined;
  onExit: () => void;
}): JSX.Element {
  const total = props.recording.snapshots.length;
  const [display, setDisplay] = useState<DisplayState>(DEFAULT_DISPLAY_STATE);
  const [cursor, setCursor] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const sourceRef = useRef<ReplaySource | null>(null);

  // Build a single ReplaySource for this recording + this debrief
  // session. autoTick is FALSE — we drive the cursor ourselves so
  // pause / scrub / fast-forward all funnel through React state.
  const source = useMemo(() => {
    const s = new ReplaySource(props.recording, { autoTick: false, rate: 1 });
    sourceRef.current = s;
    return s;
  }, [props.recording]);

  // Connect once.
  useEffect(() => {
    void source.connect();
    return () => {
      void source.disconnect();
    };
  }, [source]);

  // When the React-side cursor changes, push the source to the same
  // index so its subscribers (PPI, chart) see the snapshot.
  useEffect(() => {
    source.seek(cursor);
  }, [cursor, source]);

  // Playback loop: bumps the cursor at the recording's tick rate
  // multiplied by the playback rate.
  useEffect(() => {
    if (!playing) return;
    const tickMs = 1000 / props.recording.metadata.tickRateHz / rate;
    const id = setInterval(() => {
      setCursor((c) => {
        const next = c + 1;
        if (next >= total) {
          setPlaying(false);
          return c;
        }
        return next;
      });
    }, Math.max(16, tickMs));
    return () => clearInterval(id);
  }, [playing, rate, props.recording.metadata.tickRateHz, total]);

  const snapshot: WorldSnapshot | undefined = props.recording.snapshots[cursor];
  const elapsedSec = snapshot?.simTimeSeconds ?? 0;
  const totalSec =
    props.recording.snapshots[total - 1]?.simTimeSeconds ?? elapsedSec;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)",
        gridTemplateRows: "minmax(0, 1fr) auto",
        gap: 16,
        width: "100%",
        height: "100%",
        minHeight: 0,
      }}
    >
      <div style={{ minWidth: 0, minHeight: 0, position: "relative" }}>
        <PPI
          source={source}
          area={props.area}
          scenarioId={props.recording.metadata.scenarioId}
          display={display}
          onDisplayChange={setDisplay}
        />
      </div>
      <div style={{ minWidth: 0, minHeight: 0 }}>
        <ChartView
          source={source}
          area={props.area}
          display={display}
          onDisplayChange={setDisplay}
        />
      </div>
      <div
        style={{
          gridColumn: "1 / 3",
          background: "var(--bg-card)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-md)",
          padding: "10px 14px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: 12,
            color: "var(--text-muted)",
          }}
        >
          <strong style={{ color: "var(--accent)", fontSize: 13 }}>
            DEBRIEF
          </strong>
          <span style={{ fontFamily: "var(--font-mono)" }}>
            {formatSec(elapsedSec)} / {formatSec(totalSec)} ·{" "}
            {cursor + 1}/{total}
          </span>
          <Stack direction="row" gap={6}>
            <button
              onClick={() => downloadRecording(props.recording)}
              style={{
                background: "transparent",
                color: "var(--text)",
                border: "1px solid var(--border-strong)",
                borderRadius: "var(--radius-sm)",
                padding: "4px 10px",
                fontSize: 11,
                cursor: "pointer",
              }}
              title="Download the recording as a JSON file"
            >
              ⬇ Download
            </button>
            <button
              onClick={props.onExit}
              style={{
                background: "transparent",
                color: "var(--text)",
                border: "1px solid var(--border-strong)",
                borderRadius: "var(--radius-sm)",
                padding: "4px 10px",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              Exit debrief
            </button>
          </Stack>
        </div>
        <input
          type="range"
          min={0}
          max={Math.max(0, total - 1)}
          value={cursor}
          onChange={(e) => {
            setPlaying(false);
            setCursor(parseInt(e.target.value, 10));
          }}
          style={{ width: "100%", accentColor: "var(--accent)" }}
          aria-label="Scrub timeline"
        />
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button
            onClick={() => setPlaying((p) => !p)}
            style={btn(playing ? "var(--accent)" : "transparent", playing ? "var(--accent-fg)" : "var(--text)")}
          >
            {playing ? "⏸ Pause" : "▶ Play"}
          </button>
          <button onClick={() => setCursor(0)} style={btn()}>
            ⏮
          </button>
          <button onClick={() => setCursor(Math.max(0, cursor - 10))} style={btn()}>
            ‹‹
          </button>
          <button onClick={() => setCursor(Math.min(total - 1, cursor + 10))} style={btn()}>
            ››
          </button>
          <button onClick={() => setCursor(total - 1)} style={btn()}>
            ⏭
          </button>
          <span style={{ marginLeft: 12, fontSize: 11, color: "var(--text-muted)" }}>
            Speed:
          </span>
          {[0.5, 1, 2, 4, 8].map((s) => (
            <button
              key={s}
              onClick={() => setRate(s)}
              style={btn(
                Math.abs(rate - s) < 1e-6 ? "var(--accent)" : "transparent",
                Math.abs(rate - s) < 1e-6 ? "var(--accent-fg)" : "var(--text)",
              )}
            >
              {s}×
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stack(props: {
  direction?: "row" | "column";
  gap?: number;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: props.direction ?? "column",
        gap: props.gap ?? 4,
      }}
    >
      {props.children}
    </div>
  );
}

function downloadRecording(recording: Recording): void {
  // Pretty-print to make the file diff-friendly. Recordings are
  // typically a few MB; modern browsers handle the encode + Blob
  // creation without complaint.
  const json = JSON.stringify(recording, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const safe = recording.metadata.scenarioId.replace(/[^a-z0-9-]/gi, "-");
  const ts = recording.metadata.capturedAt.replace(/[:.]/g, "-");
  a.download = `recording-${safe}-${ts}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function btn(bg = "transparent", color = "var(--text)"): React.CSSProperties {
  return {
    background: bg,
    color,
    border: "1px solid var(--border-strong)",
    borderRadius: "var(--radius-sm)",
    padding: "5px 10px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  };
}

function formatSec(sec: number): string {
  const mm = Math.floor(sec / 60).toString();
  const ss = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${mm}:${ss}`;
}
